import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
  FiBell,
  FiChevronDown,
  FiSettings,
  FiHelpCircle,
  FiLogOut,
  FiUser,
  FiMenu
} from 'react-icons/fi';
import config from '../config/frontend.config';
import { useNotifications } from '../contexts/NotificationContext';
import { useMobileMenu } from '../contexts/MobileMenuContext';
import NotificationDropdown from './NotificationDropdown';
import SearchBar from './SearchBar';
import './Topbar.css';

function Topbar() {
  const navigate = useNavigate();
  const { openMobileMenu } = useMobileMenu();

  // Get user data using config helper
  const userData = config.getUserData();
  const isAuthenticated = config.isAuthenticated();

  const [isAccountDropdownOpen, setIsAccountDropdownOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Notification hook
  const { unreadCount } = useNotifications();

  const dropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const notificationTriggerRef = useRef(null);

  // Track window width for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Click away to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsAccountDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);



  const handleLogout = () => {
    setIsAccountDropdownOpen(false);
    Swal.fire({
      title: 'Are you sure?',
      text: "You will be logged out.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#4f46e5',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, log me out!'
    }).then((result) => {
      if (result.isConfirmed) {
        // Clear all auth data
        config.clearAuthData();

        // Force a page reload to ensure proper navigation
        window.location.href = '/auth';
      }
    });
  };

  const getInitials = (firstName, lastName) => {
    const first = firstName ? firstName.charAt(0).toUpperCase() : '';
    const last = lastName ? lastName.charAt(0).toUpperCase() : '';
    return `${first}${last}` || <FiUser />;
  }

  const getUserName = () => {
    if (userData) {
      return `${userData.FirstName || ''} ${userData.LastName || ''}`.trim() || userData.EmailAddress || 'User';
    }
    return 'User';
  };

  const getUserEmail = () => {
    return userData?.EmailAddress || '';
  };

  const getUserRole = () => {
    const role = config.getUserRole();
    if (role === 'superadmin') {
      return 'Super Admin';
    } else if (role === 'admin') {
      return 'Admin';
    } else if (role === 'owner') {
      return 'Owner';
    } else {
      return 'Member';
    }
  };

  // Responsive helper functions
  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth <= 1024 && windowWidth > 768;
  const isDesktop = windowWidth > 1024;

  return (
    <header className="topbar">
      {/* Left Section */}
      <div className="topbar-left">
        <div className="workspace-section">
          <Link to="/" className="workspace-title-link">
            <h1 className="workspace-title">MailMaster</h1>
          </Link>
          {isAuthenticated && isDesktop && (
            <div className="workspace-badge">
              <span className="badge-dot"></span>
              <span className="badge-text">Pro Plan</span>
            </div>
          )}
        </div>
      </div>

      {/* Center Section - Search */}
      <div className="topbar-center">
        <SearchBar 
          placeholder="Search campaigns, templates, contacts..."
          isMobile={isMobile}
          onSearch={() => {}}
        />
      </div>

      {/* Right Section */}
      <div className="topbar-right">
        {/* Auth Actions for non-authenticated users */}
        {!isAuthenticated ? (
          <div className="auth-actions">
            <Link to="/auth" className="auth-btn login-btn">Login</Link>
            <Link to="/auth" className="auth-btn signup-btn">Sign Up</Link>
          </div>
        ) : (
          <>
            {/* Desktop/Tablet View */}
            {!isMobile && (
              <>
                {/* Notification Button */}
                <div className="notification-container" ref={notificationTriggerRef}>
                  <button
                    className="custom-action-btn notification-btn"
                    onClick={() => setIsNotificationOpen(!isNotificationOpen)}
                    aria-label="Notifications"
                  >
                    <FiBell />
                    {unreadCount > 0 && (
                      <span className="notification-badge">{unreadCount}</span>
                    )}
                  </button>
                  <NotificationDropdown
                    isOpen={isNotificationOpen}
                    onClose={() => setIsNotificationOpen(false)}
                    triggerRef={notificationTriggerRef}
                  />
                </div>

                <div className="topbar-divider"></div>

                {/* User Account */}
                <div className="user-account" ref={dropdownRef}>
                  <button
                    className="user-btn"
                    onClick={() => setIsAccountDropdownOpen(!isAccountDropdownOpen)}
                    aria-label="User menu"
                  >
                    <div className="user-avatar">
                      {userData?.profilePicture ? (
                        <img 
                          src={userData.profilePicture} 
                          alt="Profile" 
                          className="profile-image"
                        />
                      ) : (
                        getInitials(userData?.FirstName, userData?.LastName)
                      )}
                    </div>
                    {isDesktop && (
                      <div className="user-info">
                        <span className="users-name">{getUserName()}</span>
                        <span className="users-email">{getUserEmail()}</span>
                      </div>
                    )}
                    <FiChevronDown className={`dropdown-arrow ${isAccountDropdownOpen ? 'rotated' : ''}`} />
                  </button>

                  {/* Account Dropdown */}
                  {isAccountDropdownOpen && (
                    <div className="account-dropdown" style={{ display: 'block', opacity: 1, visibility: 'visible' }}>
                      <div className="dropdown-header">
                        <div className="dropdown-avatar account-avatar large">
                          {getInitials(userData?.FirstName, userData?.LastName)}
                        </div>
                        <div>
                          <div className="dropdown-name">{getUserName()}</div>
                          <div className="dropdown-email">{getUserEmail()}</div>
                          <div className="dropdown-role">{getUserRole()}</div>
                        </div>
                      </div>
                      <div className="dropdown-menu">
                        <Link 
                          to="/settings" 
                          className="dropdown-item"
                          onClick={() => setIsAccountDropdownOpen(false)}
                        >
                          <FiSettings />
                          <span>Settings</span>
                        </Link>
                        <Link 
                          to="/help" 
                          className="dropdown-item"
                          onClick={() => setIsAccountDropdownOpen(false)}
                        >
                          <FiHelpCircle />
                          <span>Help & Support</span>
                        </Link>
                        <div className="dropdown-divider"></div>
                        <div onClick={handleLogout} className="dropdown-item logout-item">
                          <FiLogOut />
                          <span>Sign Out</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Mobile View */}
            {isMobile && (
              <>
                {/* Mobile Menu Button */}
                <button
                  className="mobile-menu-btn"
                  onClick={openMobileMenu}
                  aria-label="Open mobile menu"
                >
                  <FiMenu size={20} />
                </button>
              </>
            )}
          </>
        )}
      </div>


    </header>
  );
}

export default Topbar; 